import type { RouteObject } from "react-router-dom";

import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import StudioJobsLayout from "@/pages/(home)/(company)/studio/_shared/StudioJobsLayout";
import { TEMPLATES_ID_KEY } from "@/pages/(home)/(company)/settings/templates/_data/paths";
import {
  DocumentTemplateBuilder,
  StudioDocumentDescribe,
  StudioDocumentExisting,
  StudioDocumentsList,
  StudioDocumentTemplateGallery,
  StudioEmailTemplateBuilder,
  StudioEmailTemplateDescribe,
  StudioEmailTemplateExisting,
  StudioEmailTemplateGallery,
  StudioEmailTemplatesList,
  StudioFormBuilder,
  StudioFormDescribe,
  StudioFormExisting,
  StudioFormsList,
  StudioFormTemplateGallery,
  StudioHub,
  StudioWorkflowBasePicker,
  StudioWorkflowBuilder,
  StudioWorkflowCategoryPicker,
  StudioWorkflowDescribe,
  StudioWorkflowSetup,
  StudioWorkflowsList,
  StudioWorkflowTemplateGallery,
} from "@/utils/lazyLoadComponents";

// ─── Path Segments ────────────────────────────────────────────────────────────
export const COMPANY_STUDIO_CATEGORY_KEY = "category";
export const COMPANY_STUDIO_WORKFLOW_ID_KEY = "id";

export enum COMPANY_STUDIO_PATH_SEGMENTS {
  ROOT = "studio",
  WORKFLOWS = "workflows",
  WORKFLOWS_ALL = "all",
  WORKFLOWS_TEMPLATES = "templates",
  CATEGORY = `:${COMPANY_STUDIO_CATEGORY_KEY}`,
  WORKFLOW_ID = `:${COMPANY_STUDIO_WORKFLOW_ID_KEY}`,
  SETUP = "setup",
  BUILDER = "builder",
  FORMS = "forms",
  DOCUMENTS = "documents",
  DOCUMENTS_EXISTING = "existing",
  EMAIL_TEMPLATES = "email-templates",
}

const S = COMPANY_STUDIO_PATH_SEGMENTS;

// ─── Route Definition (inside Layout) ────────────────────────────────────────
// All Studio screens keep the app chrome (sidebar + topbar). The creation flows
// (category picker, describe) render as gradient panels in the content area,
// like the hub. Static "workflows/all" outranks "workflows/:category", so the
// listing is never captured as a category.
export const companyStudioRoutes: RouteObject = {
  path: S.ROOT,
  handle: { breadcrumb: "Studio" },
  HydrateFallback: Spinner,
  // Resumes background AI generations and shows them in a floating dock.
  Component: StudioJobsLayout,
  children: [
    { index: true, Component: StudioHub },
    {
      path: S.WORKFLOWS,
      handle: { breadcrumb: "Workflows" },
      Component: StudioWorkflowCategoryPicker,
    },
    {
      path: `${S.WORKFLOWS}/${S.WORKFLOWS_ALL}`,
      handle: { breadcrumb: "All Workflows" },
      Component: StudioWorkflowsList,
    },
    {
      path: `${S.WORKFLOWS}/${S.WORKFLOWS_TEMPLATES}`,
      handle: { breadcrumb: "Start from existing" },
      Component: StudioWorkflowBasePicker,
    },
    {
      path: `${S.WORKFLOWS}/${S.CATEGORY}`,
      handle: { breadcrumb: "New Workflow" },
      Component: StudioWorkflowDescribe,
    },
    {
      path: `${S.WORKFLOWS}/${S.CATEGORY}/${S.WORKFLOWS_TEMPLATES}`,
      handle: { breadcrumb: "Templates" },
      Component: StudioWorkflowTemplateGallery,
    },
    {
      path: `${S.WORKFLOWS}/${S.CATEGORY}/${S.SETUP}`,
      handle: { breadcrumb: "Setup" },
      Component: StudioWorkflowSetup,
    },

    // ── Documents ────────────────────────────────────────────────────────────
    // No category layer (documents aren't module-bound like workflows): the
    // card opens the describe page directly. Static children outrank nothing
    // here — all three paths are distinct literals.
    {
      path: S.DOCUMENTS,
      handle: { breadcrumb: "Documents" },
      Component: StudioDocumentDescribe,
    },
    {
      path: `${S.DOCUMENTS}/${S.WORKFLOWS_ALL}`,
      handle: { breadcrumb: "All Documents" },
      Component: StudioDocumentsList,
    },
    {
      path: `${S.DOCUMENTS}/${S.WORKFLOWS_TEMPLATES}`,
      handle: { breadcrumb: "Templates" },
      Component: StudioDocumentTemplateGallery,
    },
    {
      path: `${S.DOCUMENTS}/${S.DOCUMENTS_EXISTING}`,
      handle: { breadcrumb: "Start from existing" },
      Component: StudioDocumentExisting,
    },

    // ── Forms ──────────────────────────────────────────────────────────────
    // Like documents, no category layer: the hub card opens the describe page
    // directly. All paths are distinct literals.
    {
      path: S.FORMS,
      handle: { breadcrumb: "Forms" },
      Component: StudioFormDescribe,
    },
    {
      path: `${S.FORMS}/${S.WORKFLOWS_ALL}`,
      handle: { breadcrumb: "All Forms" },
      Component: StudioFormsList,
    },
    {
      path: `${S.FORMS}/${S.WORKFLOWS_TEMPLATES}`,
      handle: { breadcrumb: "Templates" },
      Component: StudioFormTemplateGallery,
    },
    {
      path: `${S.FORMS}/${S.DOCUMENTS_EXISTING}`,
      handle: { breadcrumb: "Start from existing" },
      Component: StudioFormExisting,
    },

    // ── Email Templates ──────────────────────────────────────────────────────
    // Like documents/forms, no category layer: the hub card opens the describe
    // page directly. All paths are distinct literals.
    {
      path: S.EMAIL_TEMPLATES,
      handle: { breadcrumb: "Email Templates" },
      Component: StudioEmailTemplateDescribe,
    },
    {
      path: `${S.EMAIL_TEMPLATES}/${S.WORKFLOWS_ALL}`,
      handle: { breadcrumb: "All Emails" },
      Component: StudioEmailTemplatesList,
    },
    {
      path: `${S.EMAIL_TEMPLATES}/${S.WORKFLOWS_TEMPLATES}`,
      handle: { breadcrumb: "Templates" },
      Component: StudioEmailTemplateGallery,
    },
    {
      path: `${S.EMAIL_TEMPLATES}/${S.DOCUMENTS_EXISTING}`,
      handle: { breadcrumb: "Start from existing" },
      Component: StudioEmailTemplateExisting,
    },
  ],
};

// ─── Route Definition (under BuilderLayout — top bar, no sidebar) ─────────────
export const companyStudioBuilderRoutes: RouteObject[] = [
  {
    path: withCompanyPrefix(
      addSlashes(S.ROOT, S.WORKFLOWS, S.CATEGORY, S.BUILDER, S.WORKFLOW_ID),
    ),
    Component: StudioWorkflowBuilder,
    // Header title shown in the top bar (otherwise falls back to "Dashboard").
    handle: { title: "Workflow Builder" },
  },
];

// ─── Route Definition (under BuilderLayout — top bar, no sidebar) ─────────────
// Wrapped in BuilderLayout in routes/index.tsx (keeps the app top bar, hides
// the sidebar) exactly like the workflow builder. The DocumentTemplateBuilder
// renders `h-full w-full` to fill that content area (NOT w-screen/h-screen) and
// draws its own thin sub-navbar beneath the app bar. Same component the Settings
// route uses; the `:templateId` param name is shared so the component reads the
// id identically from both.
export const companyStudioDocumentsBuilderRoutes: RouteObject[] = [
  {
    path: withCompanyPrefix(
      addSlashes(S.ROOT, S.DOCUMENTS, S.BUILDER, `:${TEMPLATES_ID_KEY}`),
    ),
    Component: DocumentTemplateBuilder,
    // Header title shown in the top bar (otherwise falls back to "Dashboard").
    handle: { title: "Document Builder" },
  },
];

// ─── Route Definition (under BuilderLayout — top bar, no sidebar) ─────────────
// The forms builder is a linear field-list editor in the shared BuilderShell
// chrome (palette left, config drawer right). Same `:id` param as the other
// builders; created first, edited by id.
export const companyStudioFormsBuilderRoutes: RouteObject[] = [
  {
    path: withCompanyPrefix(
      addSlashes(S.ROOT, S.FORMS, S.BUILDER, S.WORKFLOW_ID),
    ),
    Component: StudioFormBuilder,
    handle: { title: "Form Builder" },
  },
];

// ─── Route Definition (under BuilderLayout — top bar, no sidebar) ─────────────
// The email builder is a lean rich-text authoring surface in the shared
// BuilderShell (subject + preheader + body, variables + settings drawers).
// Created first, edited by id — same `:id` param as the other builders.
export const companyStudioEmailTemplatesBuilderRoutes: RouteObject[] = [
  {
    path: withCompanyPrefix(
      addSlashes(S.ROOT, S.EMAIL_TEMPLATES, S.BUILDER, S.WORKFLOW_ID),
    ),
    Component: StudioEmailTemplateBuilder,
    handle: { title: "Email Builder" },
  },
];

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMPANY_STUDIO_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(S.ROOT)),
  /** Category picker (entry when clicking Workflows on the hub). */
  WORKFLOWS: withCompanyPrefix(addSlashes(S.ROOT, S.WORKFLOWS)),
  /** Full workflows listing (reached via "View All"). */
  WORKFLOWS_ALL: withCompanyPrefix(
    addSlashes(S.ROOT, S.WORKFLOWS, S.WORKFLOWS_ALL),
  ),
  /** "Base on an existing workflow" picker (the Templates category card). */
  WORKFLOWS_TEMPLATES: withCompanyPrefix(
    addSlashes(S.ROOT, S.WORKFLOWS, S.WORKFLOWS_TEMPLATES),
  ),
  /** Describe screen for a chosen category. */
  WORKFLOW_DESCRIBE: (category: string) =>
    withCompanyPrefix(addSlashes(S.ROOT, S.WORKFLOWS, category)),
  /** Predefined system templates gallery for a category ("Use template"). */
  WORKFLOW_TEMPLATE_GALLERY: (category: string) =>
    withCompanyPrefix(
      addSlashes(S.ROOT, S.WORKFLOWS, category, S.WORKFLOWS_TEMPLATES),
    ),
  /** Setup wizard for a new workflow in a category. */
  WORKFLOW_SETUP: (category: string) =>
    withCompanyPrefix(addSlashes(S.ROOT, S.WORKFLOWS, category, S.SETUP)),
  /** Full-screen builder for a specific workflow (created first, edited by id). */
  WORKFLOW_BUILDER: (category: string, id: string | number) =>
    withCompanyPrefix(
      addSlashes(S.ROOT, S.WORKFLOWS, category, S.BUILDER, String(id)),
    ),
  /** Forms describe screen (entry when clicking Forms on the hub). */
  FORMS: withCompanyPrefix(addSlashes(S.ROOT, S.FORMS)),
  /** Full forms listing (reached via "View All"). */
  FORMS_ALL: withCompanyPrefix(addSlashes(S.ROOT, S.FORMS, S.WORKFLOWS_ALL)),
  /** Predefined form-template starters gallery ("Use template"). */
  FORMS_TEMPLATES: withCompanyPrefix(
    addSlashes(S.ROOT, S.FORMS, S.WORKFLOWS_TEMPLATES),
  ),
  /** "Base on an existing form" picker (clone a created form). */
  FORMS_EXISTING: withCompanyPrefix(
    addSlashes(S.ROOT, S.FORMS, S.DOCUMENTS_EXISTING),
  ),
  /** Full-screen form builder for a specific form (created first, edited by id). */
  FORMS_BUILDER: (id: string | number) =>
    withCompanyPrefix(addSlashes(S.ROOT, S.FORMS, S.BUILDER, String(id))),
  /** Documents describe screen (entry when clicking Documents on the hub). */
  DOCUMENTS: withCompanyPrefix(addSlashes(S.ROOT, S.DOCUMENTS)),
  /** Full documents listing (reached via "View All"). */
  DOCUMENTS_ALL: withCompanyPrefix(
    addSlashes(S.ROOT, S.DOCUMENTS, S.WORKFLOWS_ALL),
  ),
  /** Predefined document-template starters gallery ("Use template"). */
  DOCUMENTS_TEMPLATES: withCompanyPrefix(
    addSlashes(S.ROOT, S.DOCUMENTS, S.WORKFLOWS_TEMPLATES),
  ),
  /** "Base on an existing document" picker (duplicate a created template). */
  DOCUMENTS_EXISTING: withCompanyPrefix(
    addSlashes(S.ROOT, S.DOCUMENTS, S.DOCUMENTS_EXISTING),
  ),
  /** Full-screen document builder for a specific template (Studio-scoped URL). */
  DOCUMENTS_BUILDER: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(S.ROOT, S.DOCUMENTS, S.BUILDER, String(id)),
    ),
  /** Email Templates describe screen (entry when clicking Email Templates on the hub). */
  EMAIL_TEMPLATES: withCompanyPrefix(addSlashes(S.ROOT, S.EMAIL_TEMPLATES)),
  /** Full email-templates listing (reached via "View All"). */
  EMAIL_TEMPLATES_ALL: withCompanyPrefix(
    addSlashes(S.ROOT, S.EMAIL_TEMPLATES, S.WORKFLOWS_ALL),
  ),
  /** Predefined email starters gallery ("Use template"). */
  EMAIL_TEMPLATES_TEMPLATES: withCompanyPrefix(
    addSlashes(S.ROOT, S.EMAIL_TEMPLATES, S.WORKFLOWS_TEMPLATES),
  ),
  /** "Base on an existing email" picker (duplicate a created template). */
  EMAIL_TEMPLATES_EXISTING: withCompanyPrefix(
    addSlashes(S.ROOT, S.EMAIL_TEMPLATES, S.DOCUMENTS_EXISTING),
  ),
  /** Full-screen email builder for a specific template (created first, edited by id). */
  EMAIL_TEMPLATES_BUILDER: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(S.ROOT, S.EMAIL_TEMPLATES, S.BUILDER, String(id)),
    ),
});
